import { Box, Button, Icon } from "@amzn/awsui-components-console";
import React, { useEffect, useState } from "react";

import { useCart } from "@/contexts/CartContext";
import { useClipPreview } from "@/contexts/ClipPreviewContext";
import useClipMultiSelectModal from "@/hooks/clip/useClipMultiSelectModal";
import useExportClipModal from "@/hooks/export/useExportClipModal";
import useShareClipModal from "@/hooks/share/useShareClipModal";
import useTransferClipModal from "@/hooks/transfer/useTransferClipModal";

import styles from "./ClipPreviewPanel.module.css";
import { EnumeratedIconikAsset } from "./types";
import VideoPlayer from "./VideoPlayer";

const NOT_AVAILABLE = "Not available";

/** Best hi-res-first download URL for a clip, if any. */
const clipDownloadUrl = (clip: EnumeratedIconikAsset): string | undefined =>
  clip.downloadUrl || clip.proxyUrl || undefined;

/** Trigger a browser download for one clip (hi-res preferred, proxy fallback). */
const triggerDownload = (clip: EnumeratedIconikAsset): void => {
  const url = clipDownloadUrl(clip);
  if (!url) return;
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "";
  anchor.rel = "noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

/** A labelled metadata field; renders a muted placeholder when no value. */
const MetadataField: React.FC<{
  label: string;
  children?: React.ReactNode;
  hasValue: boolean;
}> = ({ label, children, hasValue }) => (
  <div className={styles.field}>
    <span className={styles.label}>{label}</span>
    {hasValue ? (
      <span className={styles.value}>{children}</span>
    ) : (
      <span className={styles.valueMuted}>{NOT_AVAILABLE}</span>
    )}
  </div>
);

const ClipPreviewPanel: React.FC = () => {
  const {
    isOpen,
    clips,
    activeClip,
    activeIndex,
    playId,
    source,
    hasNext,
    hasPrev,
    groupPosition,
    groupCount,
    crossingGroup,
    next,
    prev,
    closePreview,
  } = useClipPreview();
  const { addToCart, removeFromCart, clearCart } = useCart();

  const isCart = source === "cart";

  // Whether the next press leaves this group. Drives the double-chevron glyph
  // and the arrow's label, so the control says what it is about to do instead
  // of looking like a plain next-clip step that happens to still be enabled.
  const crossesForward = hasNext && activeIndex >= clips.length - 1;
  const crossesBackward = hasPrev && activeIndex <= 0;

  const activeAssetId = activeClip?.assetId;

  // Keep the chat's highlighted row in view as the preview moves.
  //
  // The row is located through the `[data-active-row]` marker that
  // IconikClipTable injects into the active clip's row. That marker is already a
  // cross-component contract — the row highlight in ChatMarkdownTable.module.css
  // styles `tr:has([data-active-row])` — so this reads an existing hook rather
  // than adding plumbing between the panel and the accordions.
  //
  // Scrolls only when the marker is UNAMBIGUOUS. More than one can exist: the
  // highlight gate in IconikAssetsTable matches on gameId + playId, which is not
  // unique within a conversation — the same play legitimately appears in several
  // answers (the reason `useAssets` dedups its fetches), and two multi-sport
  // cards can name the same asset id. Both matching accordions then mark their
  // row, and picking one would scroll to whichever sits earlier in the document,
  // i.e. the oldest answer — yanking the user away from the answer they are
  // working in, on every arrow press. Doing nothing is the better failure.
  //
  // The proper fix is an identity that is unique per answer, at which point this
  // guard becomes redundant rather than wrong. Until then it is enforced here
  // instead of assumed.
  //
  // `block: "nearest"` is load-bearing: it makes this a no-op whenever the row
  // is already visible, which covers stepping within a group and opening the
  // preview from a row click. It only really scrolls when navigation has landed
  // somewhere off-screen, which in practice means a group boundary.
  //
  // A collapsed group still has its marker (Cloudscape's ExpandableSection
  // hides content with CSS and `inert` rather than unmounting it), so a closed
  // accordion still counts toward the ambiguity check above.
  useEffect(() => {
    if (!isOpen || !activeAssetId) return;
    const markers = document.querySelectorAll("[data-active-row]");
    if (markers.length !== 1) return;
    const row = markers[0].closest("tr");
    if (!row) return;
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Optional call: jsdom does not implement scrollIntoView, and a missing
    // native method should not break navigation.
    row.scrollIntoView?.({
      block: "nearest",
      behavior: reduceMotion ? "auto" : "smooth",
    });
    // Positional identity, not just the clip's id. One Iconik asset can carry
    // several play ids — the registry's fields are plural (`NBAPlayIDs`,
    // `nwslPlayIds`, …) — so the same asset can be a clip in two different
    // groups, and a step between them would leave `activeAssetId` unchanged and
    // this effect asleep. `groupPosition` moves on every group step and
    // `activeIndex` on every step within one, so together they change on any
    // navigation. Both are navigation state, so this does not re-fire on
    // unrelated panel renders.
  }, [isOpen, activeAssetId, groupPosition, activeIndex]);

  const {
    ModalComponent: ExportModal,
    openSingle: openSingleExport,
    openMultiple: openMultipleExport,
  } = useExportClipModal();
  const {
    ModalComponent: ShareModal,
    openSingle: openSingleShare,
    openMultiple: openMultipleShare,
  } = useShareClipModal();
  const {
    ModalComponent: TransferModal,
    openSingle: openSingleTransfer,
    openMultiple: openMultipleTransfer,
  } = useTransferClipModal();
  const {
    ModalComponent: DownloadModal,
    open: openDownloadMultiple,
  } = useClipMultiSelectModal({
    title: "Download Clips",
    instruction: "Select the clips to download.",
    confirmLabel: "Download",
    isSelectable: (clip) => !!clipDownloadUrl(clip),
    emptyText: "None of these clips have a downloadable file.",
    onConfirm: (selected) => selected.forEach(triggerDownload),
  });
  const {
    ModalComponent: AddToCartModal,
    open: openAddToCartMultiple,
  } = useClipMultiSelectModal({
    title: "Add Clips to Cart",
    instruction: "Select the clips to add to the cart.",
    confirmLabel: "Add to Cart",
    onConfirm: (selected) => addToCart(selected),
  });

  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [cartMenuOpen, setCartMenuOpen] = useState(false);
  const [removeMenuOpen, setRemoveMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [transferMenuOpen, setTransferMenuOpen] = useState(false);

  return (
    <div className={styles.panel} data-testid="clip-preview-panel">
      {!activeClip ? (
        <Box color="text-status-inactive">No clip selected.</Box>
      ) : (
        <>
          {/* HTML5 video player (proxy preferred, hi-res fallback). Keyed by
              asset id so navigating to another clip mounts a fresh, paused
              player. */}
          <VideoPlayer
            key={activeClip.assetId}
            src={activeClip.proxyUrl || activeClip.downloadUrl || ""}
            startTime={
              activeClip.startTimeMs != null
                ? activeClip.startTimeMs / 1000
                : undefined
            }
            endTime={
              activeClip.endTimeMs != null
                ? activeClip.endTimeMs / 1000
                : undefined
            }
          />

          {/* Action grid directly under the player */}
          <div className={styles.actionGrid}>
            {/* Download: one dropdown — this clip, or a multi-select modal. */}
            <div className={styles.exportCell}>
              <button
                type="button"
                className={styles.actionButton}
                aria-haspopup="menu"
                aria-expanded={downloadMenuOpen}
                onClick={() => setDownloadMenuOpen((open) => !open)}
                onBlur={() =>
                  window.setTimeout(() => setDownloadMenuOpen(false), 150)
                }
              >
                <Icon name="download" size="small" /> Download
                <Icon name="caret-down-filled" size="small" />
              </button>
              {downloadMenuOpen && (
                <div className={styles.exportMenu} role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    disabled={!clipDownloadUrl(activeClip)}
                    onClick={() => {
                      setDownloadMenuOpen(false);
                      triggerDownload(activeClip);
                    }}
                  >
                    Download This Clip
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={() => {
                      setDownloadMenuOpen(false);
                      openDownloadMultiple(clips);
                    }}
                  >
                    Download Multiple
                  </button>
                </div>
              )}
            </div>
            {isCart ? (
              // On the Cart, the cart control mirrors the Add-to-Cart dropdown:
              // one button that drops down to remove this clip or the whole
              // cart. Removing all empties the cart and closes the preview
              // (there's nothing left to show).
              <div className={styles.exportCell}>
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-haspopup="menu"
                  aria-expanded={removeMenuOpen}
                  onClick={() => setRemoveMenuOpen((open) => !open)}
                  onBlur={() =>
                    window.setTimeout(() => setRemoveMenuOpen(false), 150)
                  }
                >
                  <Icon name="remove" size="small" /> Remove from Cart
                  <Icon name="caret-down-filled" size="small" />
                </button>
                {removeMenuOpen && (
                  <div className={styles.exportMenu} role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.exportMenuItem}
                      onClick={() => {
                        setRemoveMenuOpen(false);
                        removeFromCart([activeClip.assetId]);
                      }}
                    >
                      Remove from Cart
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.exportMenuItem}
                      onClick={() => {
                        setRemoveMenuOpen(false);
                        clearCart();
                        closePreview();
                      }}
                    >
                      Remove All from Cart
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Add to Cart: one dropdown — this clip, or a multi-select modal.
              <div className={styles.exportCell}>
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-haspopup="menu"
                  aria-expanded={cartMenuOpen}
                  onClick={() => setCartMenuOpen((open) => !open)}
                  onBlur={() =>
                    window.setTimeout(() => setCartMenuOpen(false), 150)
                  }
                >
                  <Icon name="add-plus" size="small" /> Add to Cart
                  <Icon name="caret-down-filled" size="small" />
                </button>
                {cartMenuOpen && (
                  <div className={styles.exportMenu} role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.exportMenuItem}
                      onClick={() => {
                        setCartMenuOpen(false);
                        addToCart([activeClip]);
                      }}
                    >
                      Add This Clip to Cart
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.exportMenuItem}
                      onClick={() => {
                        setCartMenuOpen(false);
                        openAddToCartMultiple(clips);
                      }}
                    >
                      Add Multiple to Cart
                    </button>
                  </div>
                )}
              </div>
            )}
            <a
              className={styles.actionButton}
              href={`https://app.iconik.io/asset/${activeClip.assetId}`}
              target="_blank"
              rel="noreferrer"
            >
              <Icon name="external" size="small" /> Open in Iconik
            </a>
            <div className={styles.exportCell}>
              <button
                type="button"
                className={styles.actionButton}
                aria-haspopup="menu"
                aria-expanded={shareMenuOpen}
                onClick={() => setShareMenuOpen((open) => !open)}
                onBlur={() =>
                  window.setTimeout(() => setShareMenuOpen(false), 150)
                }
              >
                <Icon name="share" size="small" /> Share Clip
                <Icon name="caret-down-filled" size="small" />
              </button>
              {shareMenuOpen && (
                <div className={styles.exportMenu} role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={() => {
                      setShareMenuOpen(false);
                      openSingleShare(activeClip);
                    }}
                  >
                    Share This Clip
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={() => {
                      setShareMenuOpen(false);
                      openMultipleShare(clips);
                    }}
                  >
                    Share Multiple
                  </button>
                </div>
              )}
            </div>
            <div className={styles.exportCell}>
              <button
                type="button"
                className={styles.actionButton}
                aria-haspopup="menu"
                aria-expanded={exportMenuOpen}
                onClick={() => setExportMenuOpen((open) => !open)}
                onBlur={() =>
                  // Defer so a click on a menu item registers first.
                  window.setTimeout(() => setExportMenuOpen(false), 150)
                }
              >
                <Icon name="upload" size="small" /> Export Clip
                <Icon name="caret-down-filled" size="small" />
              </button>
              {exportMenuOpen && (
                <div className={styles.exportMenu} role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={() => {
                      setExportMenuOpen(false);
                      openSingleExport(activeClip);
                    }}
                  >
                    Export This Clip
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={() => {
                      setExportMenuOpen(false);
                      openMultipleExport(clips);
                    }}
                  >
                    Export Multiple
                  </button>
                </div>
              )}
            </div>
            <div className={styles.exportCell}>
              <button
                type="button"
                className={styles.actionButton}
                aria-haspopup="menu"
                aria-expanded={transferMenuOpen}
                onClick={() => setTransferMenuOpen((open) => !open)}
                onBlur={() =>
                  window.setTimeout(() => setTransferMenuOpen(false), 150)
                }
              >
                <Icon name="arrow-right" size="small" /> Transfer Clip
                <Icon name="caret-down-filled" size="small" />
              </button>
              {transferMenuOpen && (
                <div className={styles.exportMenu} role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={() => {
                      setTransferMenuOpen(false);
                      openSingleTransfer(activeClip);
                    }}
                  >
                    Transfer This Clip
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={styles.exportMenuItem}
                    onClick={() => {
                      setTransferMenuOpen(false);
                      openMultipleTransfer(clips);
                    }}
                  >
                    Transfer Multiple
                  </button>
                </div>
              )}
            </div>
          </div>

          <hr className={styles.divider} />

          {/* Navigation */}
          <div className={styles.navArrows}>
            <Button
              variant="icon"
              iconName={crossesBackward ? "angle-left-double" : "angle-left"}
              ariaLabel={crossesBackward ? "Previous group" : "Previous clip"}
              // Enabled past this group's first clip when an earlier group is
              // available, so the arrows run continuously through the answer.
              disabled={!hasPrev || crossingGroup}
              onClick={prev}
            />
            <span className={styles.counterGroup}>
              <span className={styles.counter} data-testid="clip-counter">
                <strong>{activeIndex + 1}</strong> of {clips.length} clips
              </span>
              {/* Outer position, shown only once there is more than one group
                  to move between. This is what stops "2 of 2 clips" reading as
                  a dead end while the arrow is still live.
                  "Groups" deliberately, not "plays": a group is a play for
                  NBA/WNBA/NFL/NWSL/UCL, a point for Tennis, a whole race for
                  NASCAR (no play id at all), or a single clip on the flat
                  Asset ID route. See SPORT_MAPPINGS in the backend config. */}
              {groupCount > 1 && (
                <span
                  className={styles.groupCounter}
                  data-testid="clip-group-counter"
                >
                  {groupPosition} of {groupCount} groups
                </span>
              )}
            </span>
            <Button
              variant="icon"
              iconName={crossesForward ? "angle-right-double" : "angle-right"}
              ariaLabel={crossesForward ? "Next group" : "Next clip"}
              disabled={!hasNext || crossingGroup}
              onClick={next}
            />
          </div>

          <hr className={styles.divider} />

          {/* Metadata (dynamic per active clip, driven by the admin's
              showInPreview selection). Title + Play Description are
              structural and always shown; the rest are the admin-enabled
              read-only fields. */}
          <div className={styles.metadata}>
            <MetadataField label="Title" hasValue={!!activeClip.filename}>
              {activeClip.filename}
            </MetadataField>
            <div className={styles.field}>
              <span className={styles.label}>Play Description</span>
              {activeClip.description ? (
                // Always shown in full. styles.value carries
                // `overflow-wrap: anywhere`, so a long unbroken token wraps
                // inside the panel instead of overflowing it.
                <div className={styles.value} data-testid="play-description">
                  {activeClip.description}
                </div>
              ) : (
                <span className={styles.valueMuted}>{NOT_AVAILABLE}</span>
              )}
            </div>
            {(activeClip.previewFields ?? []).map((field) => (
              <MetadataField
                key={field.name}
                label={field.label}
                hasValue={!!field.value}
              >
                {field.value}
              </MetadataField>
            ))}
          </div>

          {/* Play footer */}
          <div className={styles.count}>
            {isCart
              ? `Cart • ${clips.length} clip${clips.length === 1 ? "" : "s"}`
              : `Play ID: ${playId} • ${clips.length} camera angles`}
          </div>

          <DownloadModal />
          <AddToCartModal />
          <ExportModal />
          <ShareModal />
          <TransferModal />
        </>
      )}
    </div>
  );
};

export default ClipPreviewPanel;
